import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useWindowDimensions } from 'react-native';
import { Easing, SharedValue, withTiming } from 'react-native-reanimated';

import { useNavigation } from '@react-navigation/native';

import { EntryInfoFragment } from '@/api/entries/query.generated';
import { useModals } from '@/feature/alerts/hooks/use-modals';
import { useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import { usePlaceEntry } from '@/feature/betslip-pickem/hooks/use-place-entry';
import { usePlaceEntryHandlers } from '@/feature/betslip-pickem/hooks/use-place-entry-handlers';
import { activeLimitsByTypeSelector, useUserLimits } from '@/feature/responsible-gaming/hooks/use-user-limits';
import { useCurrencyNumberPad } from '@/hooks/use-currency-number-pad';
import { useDebounce } from '@/hooks/use-debounce';
import { CreateEntryTimeout } from '@/hooks/use-entries';
import { useWalletStore } from '@/hooks/use-wallet';
import { RaceToPlaceEntry } from '@/types/socialFeed.generated';
import { MAX_WEB_WIDTH, MODAL_PADDINGS, isWeb } from '@/utils/constants-platform-specific';

import { RaceToPlaceAmountSelectionAndSubmit } from './RaceToPlaceAmountSelectionAndSubmit';
import { RaceToPlaceCard } from './RaceToPlaceCard';
import { RaceToPlaceModalList } from './RaceToPlaceModalList';
import { useRaceToPlaceAmountState } from './hooks/use-race-to-place-amount-state';
import { useRaceToPlaceAmountValidation } from './hooks/use-race-to-place-amount-validation';
import { useRaceToPlaceHeights } from './hooks/use-race-to-place-modal-height';
import { useRaceToPlacePicks } from './hooks/use-race-to-place-picks';
import { RaceToPlaceAmountSelectorType } from './race-to-place-constants';

type RaceToPlaceModalProps = {
    raceToPlaceEntry: RaceToPlaceEntry;
    cardHeight: SharedValue<number>;
    setShowLoadingOverlay: (show: boolean) => void;
};

export const RaceToPlaceModal = memo(
    ({ raceToPlaceEntry, cardHeight, setShowLoadingOverlay }: RaceToPlaceModalProps) => {
        const { dismiss } = useModals();
        const navigation = useNavigation();
        const setRefferer = useBetslipStore(state => state.actions.setRefferer);
        const [selector, setSelector] = useState<RaceToPlaceAmountSelectorType>('quick-amounts');

        const { displayValue, number, onNumberPress, onDecimalPress, onBackspacePress, setInputValue } =
            useCurrencyNumberPad(0);

        const cash = useWalletStore(state => state.realMoneyTotal);

        const { data: userLimits = [] } = useUserLimits({
            select: activeLimitsByTypeSelector(['SINGLE_WAGER', 'WAGER']),
        });

        const { maxAmount, errorMessage: rawErrorMessage } = useRaceToPlaceAmountValidation({
            amount: number,
            cash,
            minEntryAmount: raceToPlaceEntry.minEntryAmount,
            maxEntryAmount: raceToPlaceEntry.maxEntryAmount,
            userLimits,
        });

        const debouncedErrorMessage = useDebounce(400, rawErrorMessage);
        // Quick amounts: show error immediately (user tapped a preset, no need to debounce)
        // Number pad: debounce to avoid flickering while the user is mid-type
        const errorMessage = selector === 'number-pad' ? debouncedErrorMessage : rawErrorMessage;

        const amountState = useRaceToPlaceAmountState({
            selector,
            amount: number,
            cash,
            maxEntryAmount: maxAmount,
            errorMessage,
        });

        const { width } = useWindowDimensions();
        const { events, picks } = useRaceToPlacePicks(raceToPlaceEntry);
        const fillMeterContainerWidth = isWeb ? Math.min(width, MAX_WEB_WIDTH) - 32 - MODAL_PADDINGS : undefined;

        const {
            cardHeight: modalCardHeight,
            listMaxHeight,
            isScrollable,
        } = useRaceToPlaceHeights({
            amountState,
            selector,
            numPicks: picks.length,
        });

        useEffect(() => {
            cardHeight.value = withTiming(modalCardHeight, { duration: 200, easing: Easing.inOut(Easing.ease) });
        }, [modalCardHeight, cardHeight]);

        const dismissKeyboard = useCallback(() => {
            setSelector('quick-amounts');
        }, []);

        const { handleSuccess, handleError } = usePlaceEntryHandlers();

        const onSuccess = useCallback(
            async (entry: EntryInfoFragment) => {
                setShowLoadingOverlay(false);
                await handleSuccess(entry);
                dismiss();
                navigation.navigate('FantasyPickSlipStack', {
                    screen: 'SubmitEntry',
                    params: {
                        amount: number,
                        picks,
                        entry,
                        isRaceToPlaceEntry: true,
                        multiplier: `${raceToPlaceEntry.boostedMultiplier}x`,
                    },
                });
            },
            [
                handleSuccess,
                dismiss,
                navigation,
                number,
                picks,
                setShowLoadingOverlay,
                raceToPlaceEntry.boostedMultiplier,
            ]
        );

        const onError = useCallback(
            (error: unknown) => {
                setShowLoadingOverlay(false);
                if (error instanceof CreateEntryTimeout) {
                    dismiss();
                    navigation.navigate('FantasyPickSlipStack', {
                        screen: 'SubmitEntry',
                        params: {
                            amount: number,
                            picks,
                            isTimeout: true,
                            isRaceToPlaceEntry: true,
                        },
                    });
                    return;
                }
                handleError(error);
            },
            [handleError, dismiss, navigation, number, picks, setShowLoadingOverlay]
        );

        const { placeEntry, isLoading } = usePlaceEntry({
            picks,
            onSuccess,
            onError,
        });

        const onPlaceEntry = useCallback(() => {
            setRefferer({ raceToPlaceEntryId: raceToPlaceEntry.raceToPlaceEntryId });
            setShowLoadingOverlay(true);
            placeEntry(number);
        }, [setRefferer, raceToPlaceEntry.raceToPlaceEntryId, placeEntry, number, setShowLoadingOverlay]);

        const list = useMemo(
            () => (
                <RaceToPlaceModalList
                    picks={picks}
                    events={events}
                    maxHeight={listMaxHeight}
                    isScrollable={isScrollable}
                    isKeyboardOpen={selector === 'number-pad'}
                    dismissKeyboard={dismissKeyboard}
                />
            ),
            [picks, events, listMaxHeight, isScrollable, selector, dismissKeyboard]
        );

        const action = useMemo(
            () => (
                <RaceToPlaceAmountSelectionAndSubmit
                    onBackspacePress={onBackspacePress}
                    onDecimalPress={onDecimalPress}
                    onNumberPress={onNumberPress}
                    onPlaceEntry={onPlaceEntry}
                    selector={selector}
                    setSelector={setSelector}
                    setInputValue={setInputValue}
                    displayValue={displayValue}
                    minEntryAmount={raceToPlaceEntry.minEntryAmount}
                    maxEntryAmount={maxAmount}
                    boostedMultiplier={raceToPlaceEntry.boostedMultiplier}
                    errorMessage={errorMessage}
                    amountState={amountState}
                    amount={number}
                    isLoading={isLoading}
                />
            ),
            [
                onBackspacePress,
                onDecimalPress,
                onNumberPress,
                onPlaceEntry,
                selector,
                setSelector,
                setInputValue,
                displayValue,
                raceToPlaceEntry.minEntryAmount,
                maxAmount,
                raceToPlaceEntry.boostedMultiplier,
                errorMessage,
                amountState,
                number,
                isLoading,
            ]
        );

        return (
            <RaceToPlaceCard
                raceToPlaceEntry={raceToPlaceEntry}
                fillMeterContainerWidth={fillMeterContainerWidth}
                list={list}
                action={action}
            />
        );
    }
);
